/**
 * Environment Check Component
 * Displays a warning when required environment variables are missing
 */

import React from 'react';
import {
  Box,
  VStack,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Code,
  Heading,
  Text,
  List,
  ListItem,
  ListIcon,
} from '@chakra-ui/react';
import { FaExclamationTriangle, FaCheckCircle } from 'react-icons/fa';
import { isSupabaseConfigured } from '../config/supabase';

const EnvironmentCheck: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const isConfigured = isSupabaseConfigured();

  // If configured, render children normally
  if (isConfigured) {
    return <>{children}</>;
  }

  // If not configured, show warning overlay
  return (
    <Box minH="100vh" bg="gray.50" p={8}>
      <VStack spacing={6} maxW="800px" mx="auto" pt={20}>
        <Alert
          status="error"
          variant="subtle"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          textAlign="center"
          p={8}
          borderRadius="lg"
        >
          <AlertIcon boxSize="60px" mr={0} mb={4} />
          <AlertTitle fontSize="2xl" mb={2}>
            Environment Variables Not Configured
          </AlertTitle>
          <AlertDescription maxWidth="lg" fontSize="md">
            The application requires Supabase environment variables to function properly.
          </AlertDescription>
        </Alert>

        <Box bg="white" p={8} borderRadius="lg" shadow="md" w="100%">
          <Heading size="md" mb={4}>
            🔧 Configuration Steps
          </Heading>
          
          <Text mb={4}>
            To deploy this application on Netlify, you need to set the following environment variables:
          </Text>

          <List spacing={3} mb={6}>
            <ListItem>
              <ListIcon as={FaCheckCircle} color="green.500" />
              Go to <strong>Netlify Dashboard</strong> → Your site → <strong>Site settings</strong>
            </ListItem>
            <ListItem>
              <ListIcon as={FaCheckCircle} color="green.500" />
              Navigate to <strong>Environment variables</strong>
            </ListItem>
            <ListItem>
              <ListIcon as={FaCheckCircle} color="green.500" />
              Add the following variables:
            </ListItem>
          </List>

          <VStack align="stretch" spacing={4} mb={6}>
            <Box>
              <Text fontWeight="bold" mb={2}>Required Environment Variables:</Text>
              <Code display="block" p={3} borderRadius="md" whiteSpace="pre">
{`REACT_APP_SUPABASE_URL
Value: https://your-project.supabase.co

REACT_APP_SUPABASE_ANON_KEY
Value: your-anon-key-here`}
              </Code>
            </Box>
          </VStack>

          <Alert status="info" borderRadius="md">
            <AlertIcon />
            <Box>
              <AlertTitle fontSize="sm">Where to find these values?</AlertTitle>
              <AlertDescription fontSize="sm">
                Go to your Supabase project dashboard → Settings → API
              </AlertDescription>
            </Box>
          </Alert>
        </Box>

        <Box bg="white" p={6} borderRadius="lg" shadow="md" w="100%">
          <Heading size="sm" mb={3}>
            📋 After Setting Environment Variables:
          </Heading>
          <List spacing={2} fontSize="sm">
            <ListItem>1. Go to Netlify Dashboard → Deploys</ListItem>
            <ListItem>2. Click "Trigger deploy" → "Clear cache and deploy site"</ListItem>
            <ListItem>3. Wait for the new build to complete</ListItem>
            <ListItem>4. Refresh your browser</ListItem>
          </List>
        </Box>

        <Text fontSize="sm" color="gray.600">
          Need help? Check the <Code>NETLIFY_DEPLOYMENT.md</Code> file in your repository.
        </Text>
      </VStack>
    </Box>
  );
};

export default EnvironmentCheck;
